import React, { FC, PropsWithChildren, createContext, useEffect, useState } from 'react';

import {
    EntityList,
    GetContractDealsByIdsInput,
    GetEntitiesInput,
    Service
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';

import { toArray, uniqBy } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { ALL_SERVICE, CM_UI_SUPPORT_SERVICE_ID_LIST } from 'src/constants/context';
import { AppContext } from 'src/contexts/appContext';
import {
    IAddDealContext,
    IConfirmDealAgGridRowData,
    IRateCardErrorStates,
    IRateCardForm,
    IRateCardNameToIdObj,
    IRateCardPerServiceErrorState,
    IRateCardValues,
    IServiceToRateCardNameList,
    ISpecifyDealAgGridRowData,
    ISpecifyDealAgGridRowErrorState,
    IStatusToDealsObj,
    IVendorSelected
} from 'src/types/IAddDeal';
import { IDealsRowData } from 'src/types/IDeals';
import { getGeneralStatus } from 'src/util/grid/status';

export const defaultContextValue = {
    services: [] as Service[],
    deals: [] as IDealsRowData[],
    vendors: [] as EntityList,
    selectedVendors: [] as IVendorSelected[],
    setSelectedVendors: () => {},
    rateCardValues: {} as IRateCardValues,
    setRateCardValues: () => {},
    rateCardErrorStates: {} as IRateCardErrorStates,
    setRateCardErrorStates: () => {},
    specifyDealGridRowData: [] as ISpecifyDealAgGridRowData[],
    setSpecifyDealGridRowData: () => {},
    specifyDealGridRowErrorStates: [] as ISpecifyDealAgGridRowErrorState[],
    setSpecifyDealGridRowErrorStates: () => {},
    statusToDealsObj: {} as IStatusToDealsObj,
    setStatusToDealsObj: () => {},
    serviceToRateCardNameListObj: {} as IServiceToRateCardNameList,
    setServiceToRateCardNameListObj: () => {},
    rateCardNameToIdObj: {} as IRateCardNameToIdObj,
    setRateCardNameToIdObj: () => {},
    isCancelModalOpen: false,
    setIsCancelModalOpen: () => {},
    requireRerun: false,
    setRequireRerun: () => {}
};

export const AddDealContext = createContext<IAddDealContext>(defaultContextValue);

export const AddDealProvider: FC<PropsWithChildren> = ({ children }) => {
    const { metadata, setTopAlertMsg } = React.useContext(AppContext);
    const services = Object.values(metadata.serviceMapping).filter(
        (service) => service.serviceName !== ALL_SERVICE.NAME
    );

    const defaultStatusToDealsObj = {
        new: [] as IConfirmDealAgGridRowData[],
        updated: [] as IConfirmDealAgGridRowData[],
        canceled: [] as IConfirmDealAgGridRowData[]
    };

    const [deals, setDeals] = useState<IDealsRowData[]>([]);
    const [vendors, setVendors] = useState<EntityList>([]);
    const [selectedVendors, setSelectedVendors] = useState<IVendorSelected[]>([]);
    const [rateCardValues, setRateCardValues] = useState(getDefaultRateCardValues(services));
    const [rateCardErrorStates, setRateCardErrorStates] = useState(getDefaultRateCardErrorStates(services));
    const [specifyDealGridRowData, setSpecifyDealGridRowData] = useState<ISpecifyDealAgGridRowData[]>([]);
    const [specifyDealGridRowErrorStates, setSpecifyDealGridRowErrorStates] = useState<
        ISpecifyDealAgGridRowErrorState[]
    >([]);
    const [statusToDealsObj, setStatusToDealsObj] = useState<IStatusToDealsObj>(defaultStatusToDealsObj);
    const [serviceToRateCardNameListObj, setServiceToRateCardNameListObj] = useState<IServiceToRateCardNameList>({});
    const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
    const [requireRerun, setRequireRerun] = useState<boolean>(false);
    const [rateCardNameToIdObj, setRateCardNameToIdObj] = useState<IRateCardNameToIdObj>({});

    // fetch shared deals and entities/vendors information,
    // and construct rate card list per service (serviceToRateCardNameListObj) for rate card dropdown in Specify Deals step
    useEffect(() => {
        const contractDealsPromise = contractConfigurationClient
            .getContractDealsByIds(
                GetContractDealsByIdsInput.clone({
                    includeDeletedContractDeals: true,
                    includeIneligibleContractDeals: true
                })
            )
            .toPromise();

        const entitiesPromiseList = CM_UI_SUPPORT_SERVICE_ID_LIST.map((serviceId) => {
            return contractConfigurationClient
                .getEntitiesByIds(
                    GetEntitiesInput.clone({
                        entityType: 1,
                        serviceIds: [serviceId],
                        includeIneligibleVendorCodes: true
                    })
                )
                .toPromise();
        });

        Promise.all([contractDealsPromise, ...entitiesPromiseList])
            .then((response) => {
                const [contractDealsResponse, ...entitiesResponseList] = response;

                const dealsIncludingCancelled = contractDealsResponse.contractDeals?.reduce((accu, curr) => {
                    const { entityActiveIntervals, ...rateCardInfo } = curr;

                    const dealsArrPerRateCard: IDealsRowData[] =
                        entityActiveIntervals?.map((entityActiveInterval) => {
                            return {
                                ...rateCardInfo,
                                ...entityActiveInterval,
                                status: getGeneralStatus(entityActiveInterval)
                            } as IDealsRowData;
                        }) ?? [];
                    if (dealsArrPerRateCard.length === 0) {
                        // @ts-ignore
                        dealsArrPerRateCard.push({ ...rateCardInfo });
                    }
                    accu.push(...dealsArrPerRateCard);
                    return accu;
                }, [] as IDealsRowData[]);

                // Only use non-cancelled deals to calculate how new deals affect existing non-cancelled deals
                const deals = dealsIncludingCancelled?.filter(
                    (entityActiveInterval) => entityActiveInterval.isDeleted === false
                );

                // Get the list of rate cards/contracts per service including those which only associate with cancelled deals
                const serviceToRateCardNameSetObj = dealsIncludingCancelled!.reduce(
                    (acc, curr) => {
                        if (!acc[curr.serviceName!]) {
                            acc[curr.serviceName!] = new Set<string>();
                        }

                        // Meridian Select option value can only be string type, so stringify the object containing contractId and contractName
                        acc[curr.serviceName!].add(
                            JSON.stringify({ contractId: curr.contractId, contractName: curr.contractName })
                        );
                        return acc;
                    },
                    {} as { [key: string]: Set<string> }
                );

                const newServiceToRateCardNameListObj = {} as IServiceToRateCardNameList;
                services.forEach((service) => {
                    if (!serviceToRateCardNameSetObj[service.serviceName!]) {
                        newServiceToRateCardNameListObj[service.serviceName!] = [];
                    } else {
                        // @ts-ignore
                        newServiceToRateCardNameListObj[service.serviceName!] = toArray(
                            serviceToRateCardNameSetObj[service.serviceName!]
                        );
                    }
                });

                const newRateCardNameToIdObj = {} as IRateCardNameToIdObj;
                contractDealsResponse.contractDeals?.forEach((deal) => {
                    newRateCardNameToIdObj[deal.contractName!] = deal.contractId!;
                });

                setServiceToRateCardNameListObj(newServiceToRateCardNameListObj);
                setDeals(deals || []);
                setRateCardNameToIdObj(newRateCardNameToIdObj);

                // merge all entity/vendor object into one array and then dedup
                const vendorList = entitiesResponseList.map((response) => response.entities || []).flat();
                setVendors(uniqBy(vendorList, 'entityId'));
            })
            .catch((error: UnknownCoralError) => {
                console.error('GetEntities / GetContractDeals APIs Unexpected error', error.receivedException.message);
                setTopAlertMsg(error.receivedException.message);
            });
    }, []);

    return (
        <AddDealContext.Provider
            value={{
                services,
                deals,
                vendors,
                selectedVendors,
                setSelectedVendors,
                rateCardValues,
                setRateCardValues,
                rateCardErrorStates,
                setRateCardErrorStates,
                specifyDealGridRowData,
                setSpecifyDealGridRowData,
                specifyDealGridRowErrorStates,
                setSpecifyDealGridRowErrorStates,
                statusToDealsObj,
                setStatusToDealsObj,
                serviceToRateCardNameListObj,
                setServiceToRateCardNameListObj,
                rateCardNameToIdObj,
                setRateCardNameToIdObj,
                isCancelModalOpen,
                setIsCancelModalOpen,
                requireRerun,
                setRequireRerun
            }}
        >
            {children}
        </AddDealContext.Provider>
    );
};

export const getDefaultRateCardValues = (services: Service[]) => {
    return services.reduce((acc, curr) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        acc[curr.serviceName!] = {} as IRateCardForm;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        acc[curr.serviceName!]['service'] = curr.serviceName!;
        return acc;
    }, {} as IRateCardValues);
};

export const getDefaultRateCardErrorStates = (services: Service[]) => {
    return services.reduce((acc, curr) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        acc[curr.serviceName!] = {} as IRateCardPerServiceErrorState;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        acc[curr.serviceName!]['service'] = curr.serviceName!;
        return acc;
    }, {} as IRateCardErrorStates);
};
